import "./report.css";
import React, { useCallback, useEffect, useState, useMemo } from "react";
import { IoIosArrowRoundBack } from "react-icons/io";
import { IoEyeOutline } from "react-icons/io5";
import { Link } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";

import {
  getEmployeeStaffListPath,
  getOutletListPath,
  getAuditModuleReportPath,
} from "../../services/config";
import {
  commonGetData,
  commonGetDataOne,
  commonGetDataTwo,
  initCommonState,
} from "../../redux/slices/commonSlice";
import { fetchBranchData, initBranchState } from "../../redux/slices/branchSlice";
import { useDispatch, useSelector } from "react-redux";
import Widgets from "../../components/Widgets";
import { HasPermission } from "../../services/hasPermission.js";

const paginationPageSize = 10;

const Report = () => {
  const themeStore = useSelector((state) => state.theme);
  const isLight = themeStore.theme === "light";

  // ── React Select inline styles — completely bypasses global CSS ────────────
  const selectStyles = useMemo(() => ({
    menuPortal: (b) => ({ ...b, zIndex: 9999 }),
    container: (b) => ({ ...b, width: "100%" }),
    control: (b, s) => ({
      ...b,
      minHeight: "40px",
      height: "40px",
      backgroundColor: isLight ? "#FFFFFF" : "#1A1730",
      border: `1px solid ${s.isFocused
        ? isLight ? "#7C6FFF" : "#8660F8"
        : isLight ? "#D1D5DB" : "#3D3A58"}`,
      borderRadius: "10px",
      boxShadow: s.isFocused
        ? `0 0 0 3px ${isLight ? "rgba(124,111,255,0.15)" : "rgba(134,96,248,0.18)"}`
        : "none",
      cursor: "pointer",
      flexWrap: "nowrap",
      "&:hover": { borderColor: isLight ? "#7C6FFF" : "#8660F8" },
    }),
    valueContainer: (b) => ({
      ...b,
      height: "40px",
      padding: "0 10px",
      flexWrap: "nowrap",
      overflow: "hidden",
    }),
    // ── THE KEY FIX: force the hidden inner <input> back to zero ──────────
    input: (b) => ({
      ...b,
      // override "height:38px!important" from global CSS
      height: "auto !important",
      minHeight: "0 !important",
      maxHeight: "none !important",
      margin: "0 !important",
      padding: "0 !important",
      border: "none !important",
      background: "transparent !important",
      boxShadow: "none !important",
      outline: "none !important",
      color: isLight ? "#111827" : "#E3E2EB",
      fontFamily: "'Outfit', sans-serif",
      fontSize: "13px",
    }),
    placeholder: (b) => ({
      ...b,
      color: isLight ? "#9CA3AF" : "#9998A2",
      fontSize: "13px",
      fontFamily: "'Outfit', sans-serif",
      whiteSpace: "nowrap",
      margin: 0,
    }),
    singleValue: (b) => ({
      ...b,
      color: isLight ? "#111827" : "#E3E2EB",
      fontSize: "13px",
      fontFamily: "'Outfit', sans-serif",
    }),
    multiValue: (b) => ({
      ...b,
      backgroundColor: isLight ? "rgba(124,111,255,0.12)" : "rgba(134,96,248,0.2)",
      borderRadius: "4px",
      margin: "2px",
    }),
    multiValueLabel: (b) => ({
      ...b,
      color: isLight ? "#111827" : "#E3E2EB",
      fontSize: "12px",
      padding: "2px 4px",
    }),
    multiValueRemove: (b) => ({
      ...b,
      color: isLight ? "#6B7280" : "#9998A2",
      "&:hover": { backgroundColor: "#EF4444", color: "#fff" },
    }),
    dropdownIndicator: (b) => ({
      ...b,
      color: isLight ? "#9CA3AF" : "#9998A2",
      padding: "0 8px",
    }),
    indicatorSeparator: (b) => ({
      ...b,
      backgroundColor: isLight ? "#E5E7EB" : "#3D3A58",
    }),
    clearIndicator: (b) => ({
      ...b,
      color: isLight ? "#9CA3AF" : "#9998A2",
      "&:hover": { color: "#EF4444" },
    }),
    menu: (b) => ({
      ...b,
      backgroundColor: isLight ? "#FFFFFF" : "#19152C",
      borderRadius: "10px",
      border: `1px solid ${isLight ? "#E3E3EE" : "#3D3A58"}`,
      boxShadow: isLight ? "0 8px 24px rgba(80,80,120,0.13)" : "0 8px 32px rgba(0,0,0,0.4)",
      zIndex: 9999,
    }),
    menuList: (b) => ({ ...b, padding: "4px" }),
    option: (b, s) => ({
      ...b,
      backgroundColor: s.isSelected
        ? isLight ? "#7C6FFF" : "#8660F8"
        : s.isFocused
          ? isLight ? "rgba(124,111,255,0.08)" : "rgba(134,96,248,0.15)"
          : "transparent",
      color: s.isSelected ? "#fff" : isLight ? "#111827" : "#E3E2EB",
      borderRadius: "6px",
      fontSize: "13px",
      fontFamily: "'Outfit', sans-serif",
      padding: "9px 12px",
      cursor: "pointer",
    }),
    noOptionsMessage: (b) => ({
      ...b,
      color: isLight ? "#6B7280" : "#9998A2",
      fontSize: "13px",
    }),
  }), [isLight]);

  // ── State ─────────────────────────────────────────────────────────────────
  const user = JSON.parse(localStorage.getItem("user_data")) || {};
  const dispatch = useDispatch();
  const commonStore = useSelector((s) => s.common);
  const branchStore = useSelector((s) => s.branch);

  const [employeeList,         setEmployeeList]         = useState([]);
  const [filteredEmployeeList, setFilteredEmployeeList] = useState([]);
  const [selectedEmployee,     setSelectedEmployee]     = useState([]);
  const [branchList,           setBranchList]           = useState([]);
  const [selectedBranch,       setSelectedBranch]       = useState([]);
  const [moduleList,           setModuleList]           = useState([]);
  const [selectedModule,       setSelectedModule]       = useState([]);
  const [selectedDate,         setSelectedDate]         = useState("");
  const [outletList,           setOutletList]           = useState([]);

  // ── Fetch helpers ─────────────────────────────────────────────────────────
  const getReport = useCallback((staffId, moduleId, branchId, customDate) => {
    dispatch(commonGetDataTwo(
      getAuditModuleReportPath(user.user_id, staffId, moduleId, branchId, customDate)
    ));
  }, [dispatch, user.user_id]);

  const getEmployeeList = useCallback(() => {
    dispatch(commonGetData(`${getEmployeeStaffListPath}${user.user_id}`));
    dispatch(fetchBranchData());
    dispatch(commonGetDataOne(getOutletListPath + user.user_id));
    getReport([], [], [], []);
  }, [dispatch, user.user_id, getReport]);

  useEffect(() => { getEmployeeList(); }, [getEmployeeList]);

  useEffect(() => {
    if (commonStore.commonGetSuccess) {
      if (Array.isArray(commonStore.commonGetData) && commonStore.commonGetData.length) {
        const mapped = commonStore.commonGetData.map((r) => ({ value: r.staffId, label: r.full_name }));
        setFilteredEmployeeList(mapped);
        setEmployeeList(commonStore.commonGetData);
      } else {
        setFilteredEmployeeList([]);
        setEmployeeList([]);
      }
      dispatch(initCommonState());
    }
    if (commonStore.commonGetOneSuccess || commonStore.commonGetOneError) {
      if (commonStore.commonGetOneSuccess) {
        setModuleList(
          Array.isArray(commonStore.commonGetOneData)
            ? commonStore.commonGetOneData.map((r) => ({ value: r.id, label: r.outletName }))
            : []
        );
      }
      dispatch(initCommonState());
    }
    if (commonStore.commonGetTwoSuccess || commonStore.commonGetTwoError) {
      if (commonStore.commonGetTwoSuccess && commonStore.commonGetTwoData) {
        setOutletList(commonStore.commonGetTwoData.auditModels);
      }
      if (commonStore.commonGetTwoError) {
        toast.error(commonStore.commonGetTwoMessage);
        setOutletList([]);
      }
      dispatch(initCommonState());
    }
    if (branchStore.brnGetSuccess) {
      if (branchStore.brnGetData) {
        setBranchList(branchStore.brnGetData.map((r) => ({ value: r.branchId, label: r.branch_name })));
      }
      dispatch(initBranchState());
    }
  }, [dispatch, commonStore, branchStore]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const emp   = () => selectedEmployee.map((i) => i.value).toString();
  const mod   = () => selectedModule.map((i) => i.value).toString();
  const brn   = () => selectedBranch.map((i) => i.value).toString();

  const handleEmployeeChange = (obj) => {
    setSelectedEmployee(obj || []);
    getReport((obj||[]).map((i)=>i.value).toString(), mod(), brn(), selectedDate||[]);
  };
  const handleBranchChange = (obj) => {
    setSelectedBranch(obj || []);
    getReport(emp(), mod(), (obj||[]).map((i)=>i.value).toString(), selectedDate||[]);
  };
  const handleModulesChange = (obj) => {
    setSelectedModule(obj || []);
    getReport(emp(), (obj||[]).map((i)=>i.value).toString(), brn(), selectedDate||[]);
  };
  const handleDateChange = ({ target: { value } }) => {
    setSelectedDate(value);
    getReport(emp(), mod(), brn(), value);
  };

  const setQuestionnaireData = (data) =>
    localStorage.setItem("auditAnswer", JSON.stringify(data));

  // ── AG Grid columns ───────────────────────────────────────────────────────
  const columns = useMemo(() => [
    {
      headerName: "Title",
      flex: 1,
      autoHeight: true,
      cellRenderer: (row) => (
        <div style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "10px 0",
          lineHeight: 1.5,
          gap: "2px",
        }}>
          <span style={{
            fontSize: "14px",
            fontWeight: 600,
            color: isLight ? "#111827" : "#E3E2EB",
          }}>
            {row.data.fullName} ({row.data.employeeId})
          </span>
          <span style={{
            fontSize: "12px",
            color: isLight ? "#6B7280" : "#9998A2",
          }}>
            {row.data.auditModelName}
          </span>
        </div>
      ),
    },
    {
      headerName: "Actions",
      width: 100,
      autoHeight: true,
      cellRenderer: (row) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%", paddingTop: "10px" }}>
          {HasPermission("Audit Report", "view") && (
            <Link
              onClick={() => setQuestionnaireData(row.data)}
              to={`/audit/report/questionnaire?outletId=${row.data.outletId}&staffId=${row.data.staffId}&staffName=${row.data.fullName}`}
            >
              <IoEyeOutline className="report-eye-icon" style={{ fontSize: 22, cursor: "pointer" }} />
            </Link>
          )}
        </div>
      ),
      ignoreRowClick: true,
    },
  ], [isLight]);

  // ── Theme tokens ──────────────────────────────────────────────────────────
  const hBg  = isLight ? "#FFFFFF"  : "#0C0A18";
  const hBdr = isLight ? "#E3E3EE"  : "#2D2A45";
  const fBg  = isLight ? "#F5F5FB"  : "#100D1B";
  const txt  = isLight ? "#374151"  : "#E3E2EB";

  return (
    <div
      className="audit-report-section"
      style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}
    >
      {/* ── 1. Back-link header ─────────────────────────────────────────── */}
      <div style={{
        display: "flex",
        alignItems: "center",
        flexShrink: 0,
        height: "48px",
        padding: "0 24px",
        background: hBg,
        borderBottom: `1px solid ${hBdr}`,
      }}>
        <Link to="/audit" style={{
          display: "flex", alignItems: "center", gap: "4px",
          color: txt, fontFamily: "'Outfit',sans-serif",
          fontSize: "14px", fontWeight: 500, textDecoration: "none",
        }}>
          <IoIosArrowRoundBack style={{ fontSize: 22 }} />
          Report
        </Link>
      </div>

      {/* ── 2. Filter bar ───────────────────────────────────────────────── */}
      <div style={{
        display: "flex",
        alignItems: "center",
        flexShrink: 0,
        gap: "12px",
        height: "64px",
        padding: "0 24px",
        background: fBg,
        borderBottom: `1px solid ${hBdr}`,
        boxSizing: "border-box",
        overflowX: "auto",
      }}>
        <div className="report-select-wrap">
          <Select options={filteredEmployeeList} value={selectedEmployee} isMulti
            onChange={handleEmployeeChange} placeholder="Select a employee..."
            styles={selectStyles} menuPortalTarget={document.body} classNamePrefix="rpt" />
        </div>
        <div className="report-select-wrap">
          <Select options={branchList} value={selectedBranch} isMulti
            onChange={handleBranchChange} placeholder="Select a branch..."
            styles={selectStyles} menuPortalTarget={document.body} classNamePrefix="rpt" />
        </div>
        <div className="report-select-wrap">
          <Select options={moduleList} value={selectedModule} isMulti
            onChange={handleModulesChange} placeholder="Select a modules..."
            styles={selectStyles} menuPortalTarget={document.body} classNamePrefix="rpt" />
        </div>
        {/* date wrapper isolates input from global "html body input" rules */}
        <div className="report-date-wrap">
          <input
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            className="report-date-input"
          />
        </div>
      </div>

      {/* ── 3. Table ────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflow: "hidden", padding: "16px 24px", boxSizing: "border-box" }}>
        {commonStore.commonGetTwoLoading ? (
          <Stack spacing={1.5}>
            {[...Array(6)].map((_, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: "40px" }}>
                <Skeleton variant="text" sx={{ fontSize: "2rem" }} style={{ width: "100%" }} />
                <Skeleton variant="circular" width={34} height={34} />
              </div>
            ))}
          </Stack>
        ) : (
          <div
            className={isLight ? "ag-theme-quartz custom-grid" : "ag-theme-quartz-dark custom-grid"}
            style={{ height: "100%", width: "100%" }}
          >
            <AgGridReact
              rowData={Array.isArray(outletList) ? outletList : []}
              columnDefs={columns}
              pagination={true}
              paginationPageSize={paginationPageSize}
              defaultColDef={{ resizable: true, sortable: true }}
            />
          </div>
        )}
      </div>

      <ToastContainer position="top-right" autoClose={2000} />
    </div>
  );
};

export default Report;